import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Alert, Spinner, Modal, Form, Button } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import HeaderTwo from '../../components/HeaderTwo';
import { BreadcrumbBox } from '../../components/common/Breadcrumb';
import FooterTwo from '../../components/FooterTwo';
import { Styles, BlogModalStyles } from './styles/adminBlogs.js';
import {
    ADMIN_BLOGS_ENDPOINT,
    ADMIN_BLOGS_UPDATE_ENDPOINT,
    ADMIN_GALLERY_ENDPOINT,
    API_BASE_URL
} from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' }
];

const initialForm = {
    title: '',
    slug: '',
    authorName: '',
    excerpt: '',
    content: '',
    coverImage: '',
    tags: '',
    status: 'draft'
};

const AdminBlogs = () => {
    const history = useHistory();
    const { user, loading: authLoading } = useAuth();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalItems: 0,
        totalPages: 1
    });
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [refreshToken, setRefreshToken] = useState(0);
    const [modalState, setModalState] = useState({
        show: false,
        mode: 'add',
        blog: null,
        form: initialForm,
        saving: false,
        error: ''
    });
    const [galleryImages, setGalleryImages] = useState([]);
    const [galleryLoading, setGalleryLoading] = useState(false);
    const [galleryError, setGalleryError] = useState('');
    const [gallerySelection, setGallerySelection] = useState('');

    const isAdmin = useMemo(() => {
        if (!Array.isArray(user?.roles)) {
            return false;
        }
        return user.roles.some((role) => typeof role === 'string' && role.toLowerCase() === 'admin');
    }, [user]);

    useEffect(() => {
        if (!authLoading) {
            if (!user) {
                history.push(`${process.env.PUBLIC_URL || ''}/login`);
            }
        }
    }, [authLoading, user, history]);

    const loadGalleryImages = useCallback(async () => {
        if (!isAdmin) {
            return;
        }

        setGalleryLoading(true);
        setGalleryError('');
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            const response = await fetch(ADMIN_GALLERY_ENDPOINT, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Unable to load gallery images.');
            }

            const payload = await response.json();
            setGalleryImages(Array.isArray(payload.images) ? payload.images : []);
        } catch (err) {
            setGalleryError(err.message || 'Unable to load gallery images.');
            setGalleryImages([]);
        } finally {
            setGalleryLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        if (modalState.show) {
            loadGalleryImages();
        }
    }, [modalState.show, loadGalleryImages]);

    useEffect(() => {
        if (!modalState.show) {
            return;
        }
        const current = modalState.form.coverImage;
        if (!current) {
            setGallerySelection('');
            return;
        }

        const match = galleryImages.some((image) => {
            const absolute = image.url && image.url.startsWith('http')
                ? image.url
                : `${API_BASE_URL}${image.url || ''}`;
            return absolute === current;
        });

        setGallerySelection(match ? current : '');
    }, [modalState.show, modalState.form.coverImage, galleryImages]);

    const fetchBlogs = useCallback(async () => {
        if (!isAdmin) {
            return;
        }

        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            const params = new URLSearchParams({
                page: String(page),
                limit: String(pageSize)
            });
            if (searchTerm) {
                params.set('search', searchTerm);
            }
            if (statusFilter) {
                params.set('status', statusFilter);
            }

            const response = await fetch(`${ADMIN_BLOGS_ENDPOINT}?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.message || 'Unable to load blogs.');
            }

            const payload = await response.json();
            setBlogs(payload.blogs || []);
            if (payload.pagination) {
                const serverPage = payload.pagination.page || page;
                setPagination({
                    page: serverPage,
                    limit: payload.pagination.limit || pageSize,
                    totalItems: payload.pagination.totalItems || 0,
                    totalPages: payload.pagination.totalPages || 1
                });
                if (serverPage !== page) {
                    setPage(serverPage);
                }
            } else {
                setPagination({
                    page,
                    limit: pageSize,
                    totalItems: payload.blogs?.length || 0,
                    totalPages: 1
                });
            }
        } catch (err) {
            setError(err.message || 'Unable to load blogs.');
            setBlogs([]);
        } finally {
            setLoading(false);
        }
    }, [isAdmin, page, pageSize, searchTerm, statusFilter]);

    useEffect(() => {
        if (!authLoading && isAdmin) {
            fetchBlogs();
        }
    }, [authLoading, isAdmin, fetchBlogs, refreshToken]);

    const tableRows = useMemo(() => blogs.map((blog) => ({
        id: blog._id,
        title: blog.title || 'Untitled Blog',
        slug: blog.slug,
        status: blog.status ? blog.status.charAt(0).toUpperCase() + blog.status.slice(1) : 'Draft',
        authorName: blog.authorName || 'Admin',
        createdAt: blog.createdAt ? new Date(blog.createdAt).toLocaleString() : 'N/A',
        publishedAt: blog.publishedAt ? new Date(blog.publishedAt).toLocaleString() : 'Not Published',
        tags: Array.isArray(blog.tags) ? blog.tags : [],
        raw: blog
    })), [blogs]);

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setSearchTerm(searchInput.trim());
        setPage(1);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        if (searchTerm) {
            setSearchTerm('');
        }
    };

    const openModal = (mode, blog = null) => {
        if (mode === 'edit' && blog) {
            setModalState({
                show: true,
                mode,
                blog,
                form: {
                    title: blog.title || '',
                    slug: blog.slug || '',
                    authorName: blog.authorName || '',
                    excerpt: blog.excerpt || '',
                    content: blog.content || '',
                    coverImage: blog.coverImage || '',
                    tags: Array.isArray(blog.tags) ? blog.tags.join(', ') : '',
                    status: blog.status || 'draft'
                },
                saving: false,
                error: ''
            });
            setGallerySelection(blog.coverImage || '');
        } else {
            setModalState({
                show: true,
                mode: 'add',
                blog: null,
                form: initialForm,
                saving: false,
                error: ''
            });
            setGallerySelection('');
        }
    };

    const closeModal = () => {
        setModalState({
            show: false,
            mode: 'add',
            blog: null,
            form: initialForm,
            saving: false,
            error: ''
        });
        setGallerySelection('');
    };

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setModalState((prev) => ({
            ...prev,
            form: {
                ...prev.form,
                [name]: value
            },
            error: ''
        }));
        if (name === 'coverImage') {
            setGallerySelection('');
        }
    };

    const handleGallerySelect = (event) => {
        const selected = event.target.value;
        setGallerySelection(selected);
        if (!selected) {
            return;
        }

        setModalState((prev) => ({
            ...prev,
            form: {
                ...prev.form,
                coverImage: selected
            },
            error: ''
        }));
    };

    const submitModal = async (event) => {
        event.preventDefault();
        setModalState((prev) => ({ ...prev, saving: true, error: '' }));

        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Authentication required.');
            }

            if (!modalState.form.title.trim()) {
                throw new Error('Title is required.');
            }

            const payload = {
                title: modalState.form.title.trim(),
                slug: modalState.form.slug.trim(),
                authorName: modalState.form.authorName.trim(),
                excerpt: modalState.form.excerpt.trim(),
                content: modalState.form.content.trim(),
                coverImage: modalState.form.coverImage.trim(),
                status: modalState.form.status,
                tags: modalState.form.tags
                    ? modalState.form.tags.split(',').map((tag) => tag.trim()).filter((tag) => tag.length > 0)
                    : []
            };

            const requestOptions = {
                method: modalState.mode === 'add' ? 'POST' : 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            };

            const endpoint = modalState.mode === 'add'
                ? ADMIN_BLOGS_ENDPOINT
                : ADMIN_BLOGS_UPDATE_ENDPOINT(modalState.blog._id);

            const response = await fetch(endpoint, requestOptions);

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.message || 'Unable to save blog.');
            }

            await response.json();
            setRefreshToken((prev) => prev + 1);
            closeModal();
        } catch (err) {
            setModalState((prev) => ({ ...prev, error: err.message || 'Unable to save blog.' }));
        } finally {
            setModalState((prev) => ({ ...prev, saving: false }));
        }
    };

    return (
        <Styles>
            <BlogModalStyles />
            <div className="main-wrapper admin-blogs-page">
                <HeaderTwo />
                <BreadcrumbBox title="Manage Blogs" />

                <section className="blogs-area">
                    <Container>
                        <Row>
                            <Col md="12">
                                {!authLoading && !isAdmin && (
                                    <Alert variant="danger">You do not have permission to view this page.</Alert>
                                )}
                                {isAdmin && (
                                    <div className="card-box">
                                        <div className="actions">
                                            <Button onClick={() => openModal('add')}>Add Blog</Button>
                                        </div>
                                        <div className="filter-bar">
                                            <form onSubmit={handleSearchSubmit}>
                                                <input
                                                    type="text"
                                                    placeholder="Search by title, excerpt, or tag"
                                                    value={searchInput}
                                                    onChange={(event) => setSearchInput(event.target.value)}
                                                    disabled={loading}
                                                />
                                                <button type="submit" disabled={loading}>Search</button>
                                                <button
                                                    type="button"
                                                    className="clear-btn"
                                                    onClick={handleClearSearch}
                                                    disabled={loading || (!searchTerm && !searchInput.trim())}
                                                >
                                                    Clear
                                                </button>
                                            </form>
                                            <div className="filter-controls">
                                                <label htmlFor="blog_status_filter">Status</label>
                                                <select
                                                    id="blog_status_filter"
                                                    value={statusFilter}
                                                    onChange={(event) => {
                                                        setStatusFilter(event.target.value);
                                                        setPage(1);
                                                    }}
                                                    disabled={loading}
                                                >
                                                    <option value="">All</option>
                                                    {STATUS_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                                        {loading ? (
                                            <div className="text-center my-4">
                                                <Spinner animation="border" role="status" />
                                            </div>
                                        ) : (
                                            <div className="data-grid">
                                                {tableRows.length === 0 ? (
                                                    <Alert variant="info" className="mb-0">No blogs found.</Alert>
                                                ) : (
                                                    <>
                                                        <table>
                                                            <thead>
                                                                <tr>
                                                                    <th>Title</th>
                                                                    <th>Status</th>
                                                                    <th>Author</th>
                                                                    <th>Created</th>
                                                                    <th>Published</th>
                                                                    <th>Tags</th>
                                                                    <th>Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {tableRows.map((row) => (
                                                                    <tr key={row.id}>
                                                                        <td>
                                                                            <div className="blog-title-cell">
                                                                                <strong>{row.title}</strong>
                                                                                <span className="slug">{row.slug}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="text-capitalize">{row.status}</td>
                                                                        <td>{row.authorName}</td>
                                                                        <td>{row.createdAt}</td>
                                                                        <td>{row.publishedAt}</td>
                                                                        <td>
                                                                            {row.tags.length > 0 ? (
                                                                                <div className="tag-list">
                                                                                    {row.tags.map((tag) => (
                                                                                        <span key={tag}>{tag}</span>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <span className="muted">N/A</span>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            <button
                                                                                type="button"
                                                                                className="link-button"
                                                                                onClick={() => openModal('edit', row.raw)}
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        <div className="pagination-controls">
                                                            <div className="page-info">
                                                                Page {pagination.page} of {pagination.totalPages} · {pagination.totalItems} total
                                                            </div>
                                                            <div className="page-actions">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                                                    disabled={pagination.page <= 1 || loading}
                                                                >
                                                                    Previous
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                                                                    disabled={pagination.page >= pagination.totalPages || loading}
                                                                >
                                                                    Next
                                                                </button>
                                                                <select
                                                                    value={pageSize}
                                                                    onChange={(event) => {
                                                                        const newSize = Number(event.target.value) || 10;
                                                                        setPageSize(newSize);
                                                                        setPage(1);
                                                                    }}
                                                                    disabled={loading}
                                                                >
                                                                    {[10, 20, 50, 100].map((size) => (
                                                                        <option key={size} value={size}>
                                                                            {size} / page
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </Container>
                </section>

                <FooterTwo />
            </div>

            <Modal
                show={modalState.show}
                onHide={closeModal}
                centered
                dialogClassName="admin-blogs-modal"
                size="lg"
            >
                <Form onSubmit={submitModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>{modalState.mode === 'add' ? 'Add Blog' : 'Edit Blog'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {modalState.error && <Alert variant="danger">{modalState.error}</Alert>}
                        <Row>
                            <Col md="6">
                                <Form.Group controlId="blog_title">
                                    <Form.Label>Title</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={modalState.form.title}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md="6">
                                <Form.Group controlId="blog_slug">
                                    <Form.Label>Slug</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="slug"
                                        value={modalState.form.slug}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="leave blank to auto-generate"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="6">
                                <Form.Group controlId="blog_author">
                                    <Form.Label>Author Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="authorName"
                                        value={modalState.form.authorName}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="Admin"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md="6">
                                <Form.Group controlId="blog_status">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="status"
                                        value={modalState.form.status}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                    >
                                        {STATUS_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="12">
                                <Form.Group controlId="blog_cover">
                                    <Form.Label>Cover Image URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="coverImage"
                                        value={modalState.form.coverImage}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="https://example.com/image.jpg"
                                    />
                                    <div className="gallery-picker">
                                        <span className="muted">
                                            Paste an external URL or choose an uploaded gallery image.
                                        </span>
                                        {galleryLoading ? (
                                            <div className="gallery-status">
                                                <Spinner animation="border" size="sm" role="status" />
                                                <span>Loading gallery…</span>
                                            </div>
                                        ) : (
                                            <>
                                                <Form.Control
                                                    as="select"
                                                    value={gallerySelection}
                                                    onChange={handleGallerySelect}
                                                    disabled={modalState.saving || galleryImages.length === 0}
                                                >
                                                    <option value="">Select from gallery…</option>
                                                    {galleryImages.map((image) => {
                                                        const absolute = image.url && image.url.startsWith('http')
                                                            ? image.url
                                                            : `${API_BASE_URL}${image.url || ''}`;
                                                        return (
                                                            <option key={image.fileName} value={absolute}>
                                                                {image.fileName}
                                                            </option>
                                                        );
                                                    })}
                                                </Form.Control>
                                                {galleryImages.length === 0 && !galleryError && (
                                                    <span className="muted">No gallery images available yet.</span>
                                                )}
                                            </>
                                        )}
                                        {galleryError && (
                                            <span className="text-danger small">{galleryError}</span>
                                        )}
                                    </div>
                                    {modalState.form.coverImage && (
                                        <div className="cover-preview">
                                            <span className="preview-label">Preview</span>
                                            <img
                                                src={modalState.form.coverImage}
                                                alt="Cover preview"
                                                className="img-fluid"
                                            />
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="12">
                                <Form.Group controlId="blog_tags">
                                    <Form.Label>Tags</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="tags"
                                        value={modalState.form.tags}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="comma separated tags"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="12">
                                <Form.Group controlId="blog_excerpt">
                                    <Form.Label>Excerpt</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="excerpt"
                                        value={modalState.form.excerpt}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="Short summary of the blog"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="12">
                                <Form.Group controlId="blog_content">
                                    <Form.Label>Content</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={8}
                                        name="content"
                                        value={modalState.form.content}
                                        onChange={handleFormChange}
                                        disabled={modalState.saving}
                                        placeholder="Full blog content"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={closeModal} disabled={modalState.saving}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={modalState.saving}>
                            {modalState.saving ? 'Saving...' : 'Save Blog'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Styles>
    );
};

export default AdminBlogs;
